import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';

import { useManagedMutation } from 'utils/managedHooks';
import { UPDATE_PAYMENTS, ADD_ADDITIONAL_DETAILS, UPDATE_ADDITIONAL_DETAILS, CONFIRM_ON_HOLD } from 'store/mutations';
import { useDefaultedCurrencyFromURL } from 'utils/hooks/currency';
import {
  isBookingMultiJourney,
  isBookingWithAOP,
  isConfirmed,
  hasPendingSocialMediaPayment,
  hasConflictedTicket,
  isNewAriaBooking
} from 'utils/booking';
import { userShape, bookingShape } from 'constants/propTypes';
import { ActionButton } from 'components/ui/Button';
import Table from 'components/ui/Table';
import { bookingStatus, CUSTOMER_LANGUAGE, DEFAULT_LANGUAGE } from 'types/booking';
import AlertBox from 'components/ui/AlertBox';
import { withUser } from 'components/queries/GetUserQuery';

import { findS3Refunds, calcTotalToBeRefunded, getRefundRefId } from 'components/ui/Payments/Payments.utils';
import CancelBookingCta from './CancelBookingCta';
import GenerateTickets from './GenerateTickets';
import ExternalReferencesPopup from './ExternalReferencesPopup';
import EmailLanguage from 'components/ui/EmailLanguage';
import { POINT_OF_SALES_KEY } from 'constants/booking';
import { getLoyaltyPromoPeriod } from 'utils/loyaltyData';

import { getNonCancelledItemRefs } from './Journeys/AfterSalesCtas/AfterSalesCtas.utils';
import {
  buildBookingUrl,
  shouldOverrideRules,
  shouldCancelBeDisabled,
  shouldButtonBeDisabled,
  shouldDisableCompleteRefundCta,
  formatBookingDate
} from './BookingDetailsTable.utils';
import { Reference, CenterAlign, StyledAlertBox, StyledConfirmButton, ExternalReferencesNumber } from './BookingDetailsTable.styles';
import { colours } from 'themes/default';
import ReactTooltip from 'react-tooltip';
import { TooltipContainer } from '../VoucherDetails/VoucherDetails.styles';
import { IS_ELIGIBLE_TO_TRAVEL_PER_TRAVEL_DOCS } from '../../store/queries';
import config from 'utils/config';
import { useLazyQuery, useMutation } from '@apollo/client';
import AgentPortalError from '../../errors/AgentPortalError';

import { getExternalReferencesCount } from './BookingDetailsTable.utils';

import { productFamilyCodes } from 'types/productFamilyCodes';
import { isInMoreThanXHoursFromNow } from 'utils/dates';
import { productContainSubscriptionFare, nonCancelledProducts } from 'utils/subscriptionFares';
import { bookingContainsSuspendedPassengers } from 'utils/bookingContainsSuspendedPassengers';
import { getBookingJourneysURL } from 'utils/urls';
import { isUserEurostarAgent } from 'utils/user';
import { bookingHasPlaceholderTickets } from 'utils/bookingHasPlaceholderTickets';

import VoidBookingCta from './VoidBookingCta';
import { isVoidEligible } from './BookingDetailsTable.utils';

const { PENDING_REFUND, CANCELLED, REFUNDED, OVERPAID, VOIDED } = bookingStatus;

const IS_ADVANCED_PASSENGER_INFORMATION_ENABLED = config('ENABLE_ADVANCED_PASSENGER_INFORMATION', { canOverride: true }) === 'true';

const buildTableData = ({
  bookingLevelButtonsAreDisabled,
  booking: {
    allBookingItems,
    reference,
    displayStatus,
    passengers,
    outbound = [],
    inbound = [],
    productFamilyGroup,
    payments,
    agent: { username, organization, office },
    createTimestamp,
    additionalDetails = [],
    changeExpiryTimestamp
  },
  user: { canOverrideRules, isReadOnly },
  currency,
  booking,
  onCompleteRefund,
  loading,
  onHandleChange,
  addAdditionalDetailsError,
  updateAdditionalDetailsError,
  originalEmailPreferredLanguage,
  shouldDisableEmailLanguageChanges,
  isMultiJourneyWithAOP = false,
  isOverpaid = false,
  checkPassportRequirement = false,
  history,
  tempOnHoldConfirmOnHold,
  membershipNumber,
  showVoidCta = false,
  isVoidDisabled = false
}) => {
  if (addAdditionalDetailsError) {
    throw new Error(addAdditionalDetailsError);
  }

  if (updateAdditionalDetailsError) {
    throw new Error(updateAdditionalDetailsError);
  }

  const isBookingVoided = displayStatus === VOIDED;
  passengers = passengers.filter((pax) => allBookingItems?.find((abi) => abi.passengerId === pax.id));

  const pointOfSales = additionalDetails.find(({ key }) => key === POINT_OF_SALES_KEY)?.value;
  const officeColumnKey = pointOfSales ? 'pointOfSales' : 'agentOffice';
  const isAriaBooking = isNewAriaBooking(booking);
  const isBookingUnderpaid = booking.status === bookingStatus.UNDERPAID;

  const onConfirm = async () => {
    let url = buildBookingUrl({ displayStatus, reference, productFamilyGroup, currency, payments, changeExpiryTimestamp });

    let depositAmount = booking.onHold.depositAmount || 0;
    const totalPricePaid = booking.payments.reduce((acc, payment) => {
      if (payment.paymentStatus === 'S') acc += payment.amountWithCurrency;
      return acc;
    }, 0);

    // only if is temp on hold and deposit fully paid
    if (totalPricePaid >= depositAmount && booking.status === bookingStatus.TEMPORARILY_ON_HOLD) {
      let journeysUrl = getBookingJourneysURL(reference, membershipNumber);

      await tempOnHoldConfirmOnHold();

      history.push(journeysUrl);
      window.location.reload();

      return;
    }

    history.push(url);
  };

  return {
    pnr: <Reference>{reference}</Reference>,
    status: displayStatus,
    agentUsername: username,
    agentOrganization: organization,
    [officeColumnKey]: pointOfSales ?? office,
    salesDate: formatBookingDate(createTimestamp),
    languageEmails: (
      <EmailLanguage
        width="80%"
        ml="auto"
        mr="auto"
        onChange={onHandleChange}
        initialValue={originalEmailPreferredLanguage}
        disabled={shouldDisableEmailLanguageChanges}
      />
    ),
    entireBooking: (
      <CenterAlign>
        {!isConfirmed(displayStatus) ? (
          <>
            <div data-tip data-for="confirm-booking">
              <StyledConfirmButton
                data-cy="confirm-booking"
                bookingReference={reference}
                onClick={onConfirm}
                disabled={
                  isAriaBooking ||
                  isReadOnly ||
                  shouldButtonBeDisabled(displayStatus, bookingLevelButtonsAreDisabled) ||
                  isOverpaid ||
                  isBookingVoided ||
                  isBookingUnderpaid ||
                  bookingHasPlaceholderTickets({ booking })
                }
              >
                <FormattedMessage id="bookingDetails.confirm" />
              </StyledConfirmButton>
            </div>
            {isAriaBooking && (
              <ReactTooltip className="tooltip" id="confirm-booking" data-cy="confirm-booking-tooltip" place="bottom">
                <TooltipContainer>
                  <FormattedMessage id="confirm.onlyOnAria" />
                </TooltipContainer>
              </ReactTooltip>
            )}
          </>
        ) : (
          <GenerateTickets
            booking={booking}
            bookingReference={reference}
            passengers={passengers}
            passengerLastName={passengers[0]?.lastName}
            checkPassportRequirement={checkPassportRequirement}
            disabled={
              isReadOnly ||
              shouldButtonBeDisabled(displayStatus, bookingLevelButtonsAreDisabled) ||
              isOverpaid ||
              isBookingVoided ||
              isBookingUnderpaid ||
              bookingContainsSuspendedPassengers({ booking }) ||
              bookingHasPlaceholderTickets({ booking })
            }
          />
        )}
      </CenterAlign>
    ),
    ...(showVoidCta && {
      voidBkgCta: (
        <CenterAlign>
          <div data-tip data-for="void-booking-tooltip">
            <VoidBookingCta
              bookingReference={reference}
              booking={booking}
              disabled={
                isVoidDisabled ||
                isReadOnly ||
                shouldButtonBeDisabled(displayStatus, bookingLevelButtonsAreDisabled) ||
                isBookingVoided ||
                isOverpaid ||
                isBookingUnderpaid
              }
            />
          </div>
          {isVoidDisabled && (
            <ReactTooltip className="tooltip" id="void-booking-tooltip" data-cy="void-booking-tooltip" place="bottom">
              <TooltipContainer>
                <FormattedMessage id="voidBooking.tooltip" />
              </TooltipContainer>
            </ReactTooltip>
          )}
        </CenterAlign>
      )
    }),
    cancelBkgCta: (
      <CenterAlign>
        {displayStatus === PENDING_REFUND ? (
          <ActionButton
            data-cy="complete-refund"
            bookingReference={reference}
            disabled={
              isReadOnly ||
              shouldDisableCompleteRefundCta(payments) ||
              isOverpaid ||
              isBookingVoided ||
              isBookingUnderpaid ||
              bookingContainsSuspendedPassengers({ booking }) ||
              bookingHasPlaceholderTickets({ booking })
            }
            loading={loading}
            onClick={onCompleteRefund}
          >
            <FormattedMessage id="bookingDetails.completeRefund" />
          </ActionButton>
        ) : (
          <CancelBookingCta
            bookingReference={reference}
            overrideAftersalesRules={shouldOverrideRules({
              canOverrideRules,
              outbound,
              inbound
            })}
            disabled={
              isReadOnly ||
              isMultiJourneyWithAOP ||
              shouldButtonBeDisabled(displayStatus, bookingLevelButtonsAreDisabled) ||
              shouldCancelBeDisabled({
                canOverrideRules,
                displayStatus,
                outbound,
                inbound
              }) ||
              isOverpaid ||
              isBookingVoided ||
              isBookingUnderpaid
            }
            booking={booking}
            selectedItemRefs={getNonCancelledItemRefs({ booking })}
            status={displayStatus}
          />
        )}
      </CenterAlign>
    )
  };
};

const BookingDetailsTable = ({ booking, user, bookingLevelButtonsAreDisabled, ticketDetails }) => {
  const history = useHistory();
  const [isExternalReferencesPopupOpen, toggleExternalReferencesPopupOpen] = useState(false);

  const membershipNumber = new URLSearchParams(history.location.search).get('membershipNumber');

  const [currency] = useDefaultedCurrencyFromURL({
    availableCurrencies: user.paymentCurrencies,
    defaultCurrency: booking.currency,
    isMultiCurrency: booking.isMultiCurrency
  });

  const [updatePayments, { loading }] = useManagedMutation(UPDATE_PAYMENTS, {
    refetchQueries: ['booking'],
    awaitRefetchQueries: true
  });

  const [isEligibleToTravel, { data: isEligibleToTravelData }] = useLazyQuery(IS_ELIGIBLE_TO_TRAVEL_PER_TRAVEL_DOCS, {
    onError: (error) => {
      throw new AgentPortalError(error);
    },
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    try {
      if (booking.passengers[0].lastName?.length) {
        isEligibleToTravel({ variables: { reference: booking.reference, lastName: booking.passengers[0]?.lastName } });
      }
    } catch (error) {
      throw new AgentPortalError(error);
    }
  }, [booking, isEligibleToTravel]);

  let checkPassportRequirement = false;

  if (IS_ADVANCED_PASSENGER_INFORMATION_ENABLED && isEligibleToTravelData) {
    const products = booking.outbound
      .concat(booking.inbound)
      .map((l) => l.products)
      .flat();

    const isAnyRequired = products.some((prod) => prod.passenger.travelDocumentsRequired.some((tdr) => tdr.required === true));

    const isAnyNotCompleted = isEligibleToTravelData?.isEligibleToTravelPerTravelDocs.some((doc) => !doc.readyToTravel);

    if (isAnyRequired && isAnyNotCompleted) {
      checkPassportRequirement = true;
    }
  }

  const hasCustomerPreferredLanguage = booking?.additionalDetails?.find((ad) => ad.key === CUSTOMER_LANGUAGE);
  const originalEmailPreferredLanguage = hasCustomerPreferredLanguage?.value || DEFAULT_LANGUAGE;

  const shouldDisableEmailLanguageChanges = [CANCELLED, REFUNDED, PENDING_REFUND].includes(booking?.status);
  const isOverpaid = booking.displayStatus === OVERPAID;
  const isUnderpaid = booking.status === bookingStatus.UNDERPAID;

  const bookingProducts = nonCancelledProducts({ booking });
  const isPurchasedWithSubscription = booking.wasPurchasedWithSubscription && productContainSubscriptionFare({ products: bookingProducts });

  const [addAdditionalDetails, { error: addAdditionalDetailsError }] = useManagedMutation(ADD_ADDITIONAL_DETAILS, {
    refetchQueries: ['booking'],
    awaitRefetchQueries: true
  });
  const [updateAdditionalDetails, { error: updateAdditionalDetailsError }] = useManagedMutation(UPDATE_ADDITIONAL_DETAILS, {
    refetchQueries: ['booking'],
    awaitRefetchQueries: true
  });

  const [confirmOnHold] = useMutation(CONFIRM_ON_HOLD, {
    variables: { bookingReference: booking.reference }
  });

  const onHandleChange = async ({ value }) => {
    const variables = {
      bookingReference: booking.reference,
      additionalDetails: [
        {
          key: CUSTOMER_LANGUAGE,
          value
        }
      ]
    };

    hasCustomerPreferredLanguage
      ? await updateAdditionalDetails({
          variables
        })
      : await addAdditionalDetails({
          variables
        });
  };

  const onCompleteRefund = async () => {
    const unprocessedRefunds = findS3Refunds(booking.payments);
    const refId = getRefundRefId(booking.payments);
    const toBeRefunded = calcTotalToBeRefunded(
      booking.payments,
      booking.payments.filter((p) => p.isPayout),
      refId
    );

    if (toBeRefunded <= 0 && unprocessedRefunds.length > 0) {
      await updatePayments({
        variables: {
          bookingReference: booking.reference,
          payments: [
            {
              ref: parseInt(refId, 10),
              status: 'S',
              refundProcessed: true
            }
          ]
        }
      });
    } else {
      const url = buildBookingUrl({
        displayStatus: booking.displayStatus,
        reference: booking.reference,
        productFamilyGroup: booking.productFamilyGroup,
        currency,
        membershipNumber
      });

      history.push(url);
    }
  };

  const tempOnHoldConfirmOnHold = async () => {
    await confirmOnHold();
  };

  const isMultiJourneyWithAOP = isBookingMultiJourney(booking?.additionalDetails || []) && isBookingWithAOP(booking);

  const showVoidCta = !isUserEurostarAgent(user);
  const isVoidDisabled = !isVoidEligible({ booking, displayStatus: booking.displayStatus });

  const tableData = buildTableData({
    booking,
    user,
    bookingLevelButtonsAreDisabled,
    currency,
    onCompleteRefund,
    loading,
    isMultiJourneyWithAOP,
    onHandleChange,
    addAdditionalDetailsError,
    updateAdditionalDetailsError,
    originalEmailPreferredLanguage,
    shouldDisableEmailLanguageChanges,
    isOverpaid,
    checkPassportRequirement,
    history,
    tempOnHoldConfirmOnHold,
    membershipNumber,
    showVoidCta,
    isVoidDisabled
  });

  const { isMigratedMulti, isLoyalty, productFamilyGroup, displayStatus, outbound } = booking;

  const isSnapBooking = productFamilyGroup === productFamilyCodes.LAST_MINUTE;
  const firstConfirmedOutboundIsoDate = outbound?.find(({ cancelled }) => !cancelled)?.timing?.isoDate;
  const isSnapBookingDepartingInMoreThan48h =
    isSnapBooking && isConfirmed(displayStatus) && isInMoreThanXHoursFromNow({ isoDate: firstConfirmedOutboundIsoDate, hours: 48 });

  return (
    <>
      <Table
        headerColor={colours.Greyscale.White}
        rows={[tableData]}
        columns={Object.keys(tableData).map((key) => ({ key }))}
        formatHeader={(col) => (
          <>
            <FormattedMessage id={`bookingDetails.${col.key}`} />
            {col.key === 'pnr' && (
              <>
                {' '}
                <ExternalReferencesNumber onClick={() => toggleExternalReferencesPopupOpen(true)}>
                  ({getExternalReferencesCount({ booking })})
                </ExternalReferencesNumber>
              </>
            )}
          </>
        )}
      />
      {isMigratedMulti && (
        <StyledAlertBox variant={AlertBox.variants.ERROR} dataCy="multi-inventory-warning">
          <FormattedMessage id="bookingDetails.multiInventoryWarning" />
        </StyledAlertBox>
      )}
      {isMultiJourneyWithAOP && (
        <StyledAlertBox variant={AlertBox.variants.WARNING} dataCy="multi-journey-warning">
          <FormattedMessage id="bookingDetails.multiJourneyWarning" />
        </StyledAlertBox>
      )}
      <ExternalReferencesPopup booking={booking} isOpen={isExternalReferencesPopupOpen} toggleOpen={toggleExternalReferencesPopupOpen} />
      {isOverpaid && (
        <StyledAlertBox variant={AlertBox.variants.ERROR} dataCy="overpaid-warning">
          <FormattedMessage id="manageBooking.overpaid.message" />
        </StyledAlertBox>
      )}

      {isUnderpaid && (
        <StyledAlertBox variant={AlertBox.variants.ERROR} dataCy="underpaid-warning">
          <FormattedMessage id="bookingDetails.bookingIsUnderpaid" />
        </StyledAlertBox>
      )}

      {!!getLoyaltyPromoPeriod({ isLoyalty }) && (
        <StyledAlertBox variant={AlertBox.variants.WARNING} dataCy="loyalty-promo-warning">
          <FormattedMessage id="afterSales.loyaltyPromoWarning" />
        </StyledAlertBox>
      )}

      {hasPendingSocialMediaPayment(booking) && (
        <StyledAlertBox variant={AlertBox.variants.WARNING} dataCy="pending-social-media-warning">
          <FormattedMessage id="manageBooking.pendingSocialMediaPayment.message" />
        </StyledAlertBox>
      )}
      {hasConflictedTicket(ticketDetails) && (
        <StyledAlertBox variant={AlertBox.variants.ERROR} dataCy="conflicted-ticket-warning">
          <FormattedMessage id="manageBooking.conflictedTicket.warning" />
        </StyledAlertBox>
      )}
      {isPurchasedWithSubscription && (
        <StyledAlertBox variant={AlertBox.variants.ERROR} dataCy="purchased-with-subscription-warning">
          <FormattedMessage id="manageBooking.purchasedWithSubscription.warning" />
        </StyledAlertBox>
      )}
      {isSnapBookingDepartingInMoreThan48h && (
        <StyledAlertBox variant={AlertBox.variants.ERROR} dataCy="snap-booking-departing-in-more-than-48h">
          <FormattedMessage id="manageBooking.snapBookingDepartingInMoreThan48h.warning" />
        </StyledAlertBox>
      )}
    </>
  );
};

BookingDetailsTable.propTypes = {
  user: userShape.isRequired,
  booking: bookingShape.isRequired,
  bookingLevelButtonsAreDisabled: PropTypes.bool,
  ticketDetails: PropTypes.arrayOf(PropTypes.object)
};

export default withUser(BookingDetailsTable);
export { BookingDetailsTable as RawBookingDetailsTable };
